// Global Instructions Rule Applied!
import React from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Card, Descriptions, List, Tag, Typography } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronRight } from '@fortawesome/free-solid-svg-icons'
import { fetchDealById } from '../../../../app_modules/deals/services/dealsService.ts'
import { selectDealById } from '../../../../state_manager/state_update_actions/deals/selectors.ts'
import { fetchTasksForDeal } from '../../../../app_modules/tasks/services/tasksService.ts'
import { fetchInteractionsForDeal } from '../../../../app_modules/interactions/services/interactionsService.ts'
import { fetchFundsFiltered } from '../../../../app_modules/funds/services/fundsService.ts'
import { listFiles, FileListItem } from '../../../services/filesService.ts'
import { useRecordDetail } from '../lib/useRecordDetail.ts'
import { useRecordRecent } from '../lib/useRecordRecent.ts'
import { useSectionData } from '../lib/useSectionData.ts'
import { detailPath } from '../lib/routes.ts'
import { text, num } from '../lib/format.ts'
import { LoadingState, ErrorState, Section, SectionSpinner, SectionError } from '../components/ui.tsx'
import { TaskList } from '../components/TaskList.tsx'
import { InteractionList } from '../components/InteractionList.tsx'
import { FileList } from '../components/FileList.tsx'

/**
 * Deal detail (read-only). Reuses `fetchDealById` (slice-first via useRecordDetail) for
 * the record, then per-section lazy fetches over the existing services: tasks
 * (`fetchTasksForDeal`, with the record's embedded `prog__tasks` as first paint),
 * recent activity (`fetchInteractionsForDeal`), and attachments (`listFiles` over
 * `file_urls_deals`). Fund commitments come embedded on the record
 * (`prog__deal_commitments`, fund ids only) — fund names resolve via one funds-list
 * fetch, only when the deal has commitments.
 */
export function DealScreen(): React.JSX.Element {
  const { id } = useParams()
  const navigate = useNavigate()
  const { record: deal, loading, error } = useRecordDetail<any>(id, selectDealById, fetchDealById)
  useRecordRecent('deals', id, deal?.deals__name, deal?.accounts__name)

  const tasksState = useSectionData<any[]>(id, (key, token) => fetchTasksForDeal(key, { include_completed: true }, token))
  const activityState = useSectionData<any[]>(id, (key, token) => fetchInteractionsForDeal(key, undefined, token))
  const filesState = useSectionData<FileListItem[]>(id, (key, token) => listFiles(key, token, null, 'file_urls_deals', 'deal_id'))

  // Commitment rows carry fund ids only — resolve names from the funds list (small),
  // and only bother when the deal actually has commitments.
  const commitments: any[] = (Array.isArray(deal?.prog__deal_commitments) ? deal.prog__deal_commitments : []).filter(
    (c: any) => Number(c?.deal_commitments__amount) > 0
  )
  const fundNamesState = useSectionData<Record<string, string>>(
    id,
    async (_key, token) => {
      const res = await fetchFundsFiltered([], [], 1, 100, token, {})
      if (res.error) throw new Error(res.error)
      const map: Record<string, string> = {}
      for (const f of res.data || []) {
        if (f?.funds__id) map[String(f.funds__id)] = String(f.funds__name ?? '')
      }
      return map
    },
    commitments.length > 0
  )

  if (loading && !deal) return <LoadingState label="Loading deal…" />
  if (!deal) return <ErrorState message={error} />

  const fallbackTasks: any[] = Array.isArray(deal.prog__tasks) ? deal.prog__tasks : []
  const taskRows: any[] = tasksState.data ?? fallbackTasks
  const stageColour = deal.pipeline_stages__colour
  const hasTerms =
    deal.rounds__name != null ||
    deal.rounds__raise_amount != null ||
    deal.rounds__pre_money_valuation != null ||
    deal.rounds__post_money_valuation != null ||
    deal.prog__commitment_amount != null ||
    deal.deals__allocation_available != null

  return (
    <div style={{ padding: 12 }}>
      <Card size="small" style={{ marginBottom: 12 }}>
        <div style={{ fontWeight: 700, fontSize: 18, lineHeight: 1.25 }}>{text(deal.deals__name)}</div>
        {deal.accounts__name ? (
          <div style={{ marginTop: 4 }}>
            {deal.accounts__id ? (
              <Typography.Link onClick={() => navigate(detailPath('companies', deal.accounts__id))}>
                {text(deal.accounts__name)}
              </Typography.Link>
            ) : (
              <span style={{ color: 'rgba(0,0,0,0.45)' }}>{text(deal.accounts__name)}</span>
            )}
          </div>
        ) : null}
        {deal.pipeline_deals__stage__label ? (
          <div style={{ marginTop: 10 }}>
            <Tag color={stageColour || 'default'} style={stageColour ? { color: '#fff', borderColor: stageColour } : undefined}>
              {text(deal.pipeline_deals__stage__label)}
            </Tag>
          </div>
        ) : null}
      </Card>

      {hasTerms ? (
        <Section title="Terms">
          <Descriptions column={1} size="small" colon={false} labelStyle={{ width: '45%' }}>
            {deal.rounds__name ? <Descriptions.Item label="Round">{text(deal.rounds__name)}</Descriptions.Item> : null}
            {deal.rounds__funding_stage__label ? (
              <Descriptions.Item label="Stage">{text(deal.rounds__funding_stage__label)}</Descriptions.Item>
            ) : null}
            {deal.rounds__raise_amount != null ? (
              <Descriptions.Item label="Raise amount">{num(deal.rounds__raise_amount)}</Descriptions.Item>
            ) : null}
            {deal.rounds__pre_money_valuation != null ? (
              <Descriptions.Item label="Pre-money">{num(deal.rounds__pre_money_valuation)}</Descriptions.Item>
            ) : null}
            {deal.rounds__post_money_valuation != null ? (
              <Descriptions.Item label="Post-money">{num(deal.rounds__post_money_valuation)}</Descriptions.Item>
            ) : null}
            {deal.rounds__security_type__label ? (
              <Descriptions.Item label="Security">{text(deal.rounds__security_type__label)}</Descriptions.Item>
            ) : null}
            {deal.rounds__round_close_date ? (
              <Descriptions.Item label="Close date">{text(deal.rounds__round_close_date)}</Descriptions.Item>
            ) : null}
            {deal.prog__commitment_amount != null ? (
              <Descriptions.Item label="Commitment">{num(deal.prog__commitment_amount)}</Descriptions.Item>
            ) : null}
            {deal.prog__valuation != null ? (
              <Descriptions.Item label="Valuation">{num(deal.prog__valuation)}</Descriptions.Item>
            ) : null}
            {deal.deals__allocation_available != null ? (
              <Descriptions.Item label="Allocation">{num(deal.deals__allocation_available)}</Descriptions.Item>
            ) : null}
          </Descriptions>
        </Section>
      ) : null}

      {commitments.length > 0 ? (
        <Section title="Commitments">
          {fundNamesState.data === null && fundNamesState.loading ? (
            <SectionSpinner />
          ) : fundNamesState.data === null && fundNamesState.error ? (
            <SectionError message="Couldn't load fund names." onRetry={fundNamesState.retry} />
          ) : (
            <List
              size="small"
              dataSource={commitments}
              rowKey={(c: any) => String(c.deal_commitments__id)}
              renderItem={(c: any) => {
                const fundId = c.deal_commitments__fund_id
                const fundName = fundId ? fundNamesState.data?.[String(fundId)] : null
                const clickable = Boolean(fundId)
                return (
                  <List.Item
                    onClick={clickable ? () => navigate(detailPath('funds', fundId)) : undefined}
                    style={{ cursor: clickable ? 'pointer' : 'default', paddingLeft: 4, paddingRight: 4 }}
                  >
                    <List.Item.Meta title={text(fundName || 'Fund')} description={`Committed ${num(c.deal_commitments__amount)}`} />
                    {clickable ? <FontAwesomeIcon icon={faChevronRight} style={{ color: 'rgba(0,0,0,0.25)' }} /> : null}
                  </List.Item>
                )
              }}
            />
          )}
        </Section>
      ) : null}

      <Section title="Tasks">
        {tasksState.data === null && tasksState.loading && fallbackTasks.length === 0 ? (
          <SectionSpinner />
        ) : tasksState.data === null && tasksState.error && fallbackTasks.length === 0 ? (
          <SectionError message="Couldn't load tasks." onRetry={tasksState.retry} />
        ) : (
          <TaskList tasks={taskRows} />
        )}
      </Section>

      <Section title="Files">
        {filesState.data === null && filesState.loading ? (
          <SectionSpinner />
        ) : filesState.data === null && filesState.error ? (
          <SectionError message="Couldn't load files." onRetry={filesState.retry} />
        ) : (
          <FileList files={filesState.data ?? []} />
        )}
      </Section>

      <Section title="Recent activity">
        {activityState.data === null && activityState.loading ? (
          <SectionSpinner />
        ) : activityState.data === null && activityState.error ? (
          <SectionError message="Couldn't load activity." onRetry={activityState.retry} />
        ) : (
          <InteractionList interactions={activityState.data ?? []} />
        )}
      </Section>
    </div>
  )
}

export default DealScreen
